import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Minus, Plus, ChevronLeft } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { ProductCard } from '../components/ProductCard';
import { makeCacheKey, readCache, writeCache } from '../lib/productCache';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { addToCart } = useCart();
  const { token } = useAuth();
  const [product, setProduct] = useState(location.state?.product || null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(!location.state?.product);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [adding, setAdding] = useState(false);
  const hasLocalProduct = Boolean(location.state?.product);

  useEffect(() => {
    const cacheKey = makeCacheKey('product', { id: productId });
    const cachedProduct = readCache(cacheKey);

    if (cachedProduct) {
      setProduct(cachedProduct);
      setLoading(false);
    }

    const fetchProduct = async () => {
      try {
        setError(null);
        if (!cachedProduct && !hasLocalProduct) {
          setLoading(true);
        }
        const response = await axios.get(`${API}/products/${productId}`, { timeout: 15000 });
        setProduct(response.data);
        writeCache(cacheKey, response.data);

        const relatedResponse = await axios.get(`${API}/products`, {
          params: { category: response.data.category, limit: 4 },
          timeout: 15000
        });
        setRelatedProducts(relatedResponse.data.filter(p => p.id !== productId).slice(0, 3));
      } catch (error) {
        console.error('Error fetching product:', error);
        if (!hasLocalProduct && !cachedProduct) {
          setError('Product not found');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [productId, hasLocalProduct]);

  const handleAddToCart = async () => {
    try {
      if (!token) {
        toast.info('Please sign in to add items to your cart.');
        navigate(`/login?redirect=/product/${product.id}`);
        return;
      }
      setAdding(true);
      await addToCart(product.id, quantity, product);
      toast.success('Added to cart');
    } catch (error) {
      if (error?.code === 'AUTH_REQUIRED') {
        toast.info('Please sign in to add items to your cart.');
        navigate(`/login?redirect=/product/${product.id}`);
        return;
      }
      toast.error('Failed to add to cart');
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
        <p className="text-leather-700 mb-4">{error || 'Product not found'}</p>
        <button onClick={() => navigate('/shop/belts')} className="btn-primary">Back to Shop</button>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="product-page">
      {/* Breadcrumb */}
      <div className="px-6 md:px-12 lg:px-24 py-6 border-b border-cream-200">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-leather-600 hover:text-leather-900 transition-colors"
          data-testid="back-btn"
        >
          <ChevronLeft className="w-4 h-4" />
          <span className="text-sm">Back</span>
        </button>
      </div>

      {/* Product Details */}
      <section className="px-6 md:px-12 lg:px-24 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Images */}
          <div className="space-y-4" data-testid="product-images">
            <div className="aspect-square bg-cream-100 overflow-hidden">
              <img
                src={product.images?.[selectedImage] || '/placeholder.jpg'}
                alt={product.name}
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
            {product.images?.length > 1 && (
              <div className="flex flex-wrap gap-3">
                {product.images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`w-20 h-20 bg-cream-100 overflow-hidden border-2 transition-colors ${
                      selectedImage === index ? 'border-leather-900' : 'border-transparent'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="lg:sticky lg:top-24 lg:self-start" data-testid="product-details">
            <p className="text-xs tracking-[0.3em] uppercase text-leather-400 mb-2">
              {product.category?.replace('_', ' ')}
              {product.subcategory && ` / ${product.subcategory}`}
            </p>
            <h1 className="font-heading text-3xl md:text-4xl text-leather-900 mb-4" data-testid="product-name">
              {product.name}
            </h1>
            <p className="text-2xl text-leather-900 font-medium mb-6" data-testid="product-price">
              ${product.price?.toFixed(2)}
            </p>
            
            <p className="text-leather-600 leading-relaxed mb-8" data-testid="product-description">
              {product.description}
            </p>

            {/* Stock Status */}
            <div className="mb-8">
              {product.stock > 0 ? (
                <p className="text-sm text-green-700 flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  In Stock ({product.stock} available)
                </p>
              ) : (
                <p className="text-sm text-red-700 flex items-center gap-2">
                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                  Out of Stock
                </p>
              )}
            </div>

            {/* Quantity & Add to Cart */}
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <div className="flex items-center border border-cream-200">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="w-12 h-12 flex items-center justify-center hover:bg-cream-100 transition-colors"
                  data-testid="decrease-qty"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-12 text-center" data-testid="quantity">{quantity}</span>
                <button
                  onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                  className="w-12 h-12 flex items-center justify-center hover:bg-cream-100 transition-colors"
                  data-testid="increase-qty"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              <button
                onClick={handleAddToCart}
                disabled={adding || product.stock === 0}
                className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="add-to-cart-btn"
              >
                {adding ? 'Adding...' : product.stock === 0 ? 'Out of Stock' : 'Add to Cart'}
              </button>
            </div>

            {/* Tags */}
            {product.tags?.length > 0 && (
              <div className="border-t border-cream-200 pt-6">
                <p className="text-xs tracking-widest uppercase text-leather-400 mb-3">Tags</p>
                <div className="flex flex-wrap gap-2">
                  {product.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-cream-100 text-leather-600 text-sm">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="px-6 md:px-12 lg:px-24 py-16 bg-cream-100" data-testid="related-products">
          <h2 className="font-heading text-2xl md:text-3xl text-leather-900 mb-10">You May Also Like</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {relatedProducts.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
